import React, { Dispatch, SetStateAction, useCallback } from 'react';

import { Box, DropdownMenu, Icon, Text } from '@betterment/samba-react';

export type ComparisonDropdownProps = {
  benchmarks: Array<{
    id: string;
    name: string;
    group: {
      id: string;
      name: string;
      description?: string | null;
    };
  }>;
  selectedBenchmarkId: string | null;
  onBenchmarkChange: Dispatch<SetStateAction<string | null>>;
};

const groupBenchmarksByGroupId = (
  benchmarks: ComparisonDropdownProps['benchmarks']
): Array<{ group: (typeof benchmarks)[0]['group']; benchmarks: typeof benchmarks }> => {
  const grouped = benchmarks.reduce(
    (acc, benchmark) => {
      const groupId = benchmark.group.id;
      if (!(groupId in acc)) {
        acc[groupId] = {
          group: benchmark.group,
          benchmarks: [],
        };
      }
      acc[groupId].benchmarks.push(benchmark);
      return acc;
    },
    {} as Record<string, { group: (typeof benchmarks)[0]['group']; benchmarks: typeof benchmarks }>
  );

  return Object.values(grouped);
};

export const ComparisonDropdown: React.FC<ComparisonDropdownProps> = ({
  benchmarks,
  selectedBenchmarkId,
  onBenchmarkChange,
}) => {
  const handleBenchmarkSelect = useCallback(
    (benchmarkId: string) => {
      onBenchmarkChange(benchmarkId === selectedBenchmarkId ? null : benchmarkId);
    },
    [selectedBenchmarkId, onBenchmarkChange]
  );

  const selectedBenchmark = benchmarks.find(b => b.id === selectedBenchmarkId);
  const groups = groupBenchmarksByGroupId(benchmarks);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenu.Trigger asChild={true}>
        <Box css={{ display: 'flex', alignItems: 'center', pointerEvents: 'auto', cursor: 'pointer' }}>
          {selectedBenchmark ? `Change comparison` : 'Add comparison'}
          <Icon
            name="CaretDown"
            size="small"
            color="icon"
            css={{
              transition: 'transform 0.1s ease-in-out',
              '[data-state="open"] &': { transform: 'rotate(180deg)' },
            }}
          />
        </Box>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        css={{
          display: 'grid',
          py: 'space.200',
          gap: 'space.100',
          maxWidth: '400px',
        }}
      >
        {groups.map((groupData, index) => (
          <React.Fragment key={groupData.group.id}>
            {index > 0 && <DropdownMenu.Separator css={{ my: 'space.200' }} />}
            <Box role="group" aria-labelledby={`group-${groupData.group.id}`}>
              <Text
                id={`group-${groupData.group.id}`}
                css={{ px: 'space.200', py: 'space.100', fontWeight: 'bold', display: 'block' }}
              >
                {groupData.group.name}
              </Text>
              {groupData.group.description && (
                <Text
                  css={{ px: 'space.200', pb: 'space.100', color: 'text.weak', whiteSpace: 'normal', display: 'block' }}
                >
                  {groupData.group.description}
                </Text>
              )}
              {groupData.benchmarks.map(benchmark => (
                <DropdownMenu.Item
                  key={benchmark.id}
                  onSelect={() => handleBenchmarkSelect(benchmark.id)}
                  css={{ display: 'flex', alignItems: 'flex-start', gap: 'space.150' }}
                >
                  {benchmark.id === selectedBenchmarkId && <Icon name="Checkmark" size="small" />}
                  {benchmark.name}
                </DropdownMenu.Item>
              ))}
            </Box>
          </React.Fragment>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
